import { AnimatePresence, motion } from "framer-motion";
import { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import AnimatedOverlay from "@Components/atoms/AnimatedOverlay";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse } from "@Types/api/error";
import { Article } from "@Types/cart";
import { convertCartFromApiToContent, convertCartFromContentToApi } from "@Utils/cart/cart";
import { errorManager } from "@Utils/error";
import { convertToOrderUpdatedProperties, setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  article: Article;
  disabled: boolean;
  display: boolean;
  onBack?: () => void;
  onDismiss: () => void;
  onRemove: (modifiedArticle: Article) => void;
  panel: boolean;
}

interface ContainerProps {
  article: Article;
  display: boolean;
  onBack?: () => void;
  onDismiss: () => void;
  panel: boolean;
}

export const CartArticleRemove: FunctionComponent<Props> = ({
  article,
  disabled,
  display,
  onBack,
  onDismiss,
  onRemove,
  panel,
}) => {
  const intl = useIntl();

  const text = (
    <>
      <div className="CartArticleRemove-title">
        {intl.formatMessage({ id: "cheerz_CartArticleRemove_title" })}
      </div>
      <div className="CartArticleRemove-description">
        {intl.formatMessage({ id: "cheerz_CartArticleRemove_description" })}
      </div>
    </>
  );

  return (
    <>
      {display && !panel && (
        <div className="CartArticleRemove-screen">
          {text}
          <div className="CartArticleRemove-screen-buttons">
            <ButtonSmily
              disabled={disabled}
              onClick={() => onRemove(article)}
              size="m"
              type="primary"
            >
              {intl.formatMessage({ id: "cheerz_CartArticleRemove_button_delete" })}
            </ButtonSmily>
            <ButtonSmily disabled={disabled} onClick={onBack} size="m" type="tertiary">
              {intl.formatMessage({ id: "cheerz_CartArticleRemove_button_cancel" })}
            </ButtonSmily>
          </div>
        </div>
      )}

      <AnimatePresence>
        {display && panel && (
          <motion.div>
            <AnimatedOverlay onClick={onDismiss} />
            <motion.div
              animate={{
                x: 0,
              }}
              className="CartArticleRemove-panel"
              exit={{ x: "100%" }}
              initial={{ x: "100%" }}
              transition={{
                damping: 40,
                stiffness: 300,
                type: "spring",
              }}
            >
              {text}
              <div className="CartArticleRemove-panel-buttons">
                <ButtonSmily disabled={disabled} onClick={onDismiss} size="xs" type="secondary">
                  {intl.formatMessage({ id: "cheerz_CartArticleRemove_button_cancel" })}
                </ButtonSmily>
                <ButtonSmily
                  disabled={disabled}
                  onClick={() => onRemove(article)}
                  size="xs"
                  type="primary"
                >
                  {intl.formatMessage({ id: "cheerz_CartArticleRemove_button_delete" })}
                </ButtonSmily>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const CartArticleRemoveContainer: FunctionComponent<ContainerProps> = ({
  article,
  display,
  onBack,
  onDismiss,
  panel,
}) => {
  const { cart, configuration, dispatch, ui } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();

  const handleRemoveArticle = (modifiedArticle: Article) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });

    const articles = cart.articles.filter((article) => article.id !== modifiedArticle.id);
    const updatedCart = { ...cart, articles };

    configuration.apiProviders.cheerz
      .saveCart(convertCartFromContentToApi(updatedCart))
      .then((cartResponse: CheerzApiCart) => {
        dispatch({
          cart: convertCartFromApiToContent(cartResponse),
          type: "setSavedCart",
        });
        onDismiss();
        setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Checkout"));
        setDataLayerTrack("Product Removed", {
          articleCategory: modifiedArticle.product.statProductGroupTag,
          articleId: modifiedArticle.id,
          articleName: modifiedArticle.product.name,
          articlePrice: modifiedArticle.total.cents,
          articleQuantity: modifiedArticle.quantity,
          cartId: cart.orderNumber,
        });
      })
      .catch((error: ApiErrorResponse) => {
        errorManager(
          dispatchNotification,
          "CFE-001",
          error.response?.data?.error_description[0] ?? undefined
        );
        dispatch({
          disabled: false,
          type: "setDisabledCart",
        });
      });
  };

  return (
    <CartArticleRemove
      article={article}
      disabled={ui.disabled}
      display={display}
      onBack={onBack}
      onDismiss={onDismiss}
      onRemove={handleRemoveArticle}
      panel={panel}
    />
  );
};

export default CartArticleRemoveContainer;
